import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import Layout from "@/components/Layout.vue";

const router = createRouter({
    history: createWebHistory(),
    routes: [
        {
            path: "/login",
            name: "Login",
            component: () => import("@/views/Login.vue"),
            meta: {
                requiresAuth: false,
                title: "登录",
            },
        },
        {
            path: "/",
            component: Layout,
            redirect: "/dashboard",
            children: [
                {
                    path: "dashboard",
                    name: "Dashboard",
                    component: () => import("@/views/Dashboard.vue"),
                    meta: {
                        requiresAuth: true,
                        title: "仪表板",
                        roles: ["admin", "lab_user"],
                    },
                },
                {
                    path: "reports",
                    meta: {
                        requiresAuth: true,
                        title: "报告管理",
                        roles: ["admin", "lab_user"],
                    },
                    children: [
                        {
                            path: "",
                            name: "ReportsList",
                            component: () => import("@/views/reports/ReportsList.vue"),
                            meta: {
                                title: "报告列表",
                            },
                        },
                        {
                            path: "upload",
                            name: "ReportsUpload",
                            component: () => import("@/views/reports/ReportsUpload.vue"),
                            meta: {
                                title: "上传报告",
                                roles: ["admin", "lab_user"],
                            },
                        },
                        {
                            path: ":id",
                            name: "ReportsDetail",
                            component: () => import("@/views/reports/ReportsDetail.vue"),
                            meta: {
                                title: "报告详情",
                            },
                        },
                        {
                            path: ":id/preview",
                            name: "ReportsPreview",
                            component: () => import("@/views/reports/ReportsPreview.vue"),
                            meta: {
                                title: "报告预览",
                            },
                        },
                    ],
                },
                {
                    path: "hospitals",
                    meta: {
                        requiresAuth: true,
                        title: "医院管理",
                        roles: ["admin"],
                    },
                    children: [
                        {
                            path: "",
                            name: "HospitalsList",
                            component: () => import("@/views/hospitals/HospitalsList.vue"),
                            meta: {
                                title: "医院列表",
                            },
                        },
                        {
                            path: "create",
                            name: "HospitalsCreate",
                            component: () => import("@/views/hospitals/HospitalsCreate.vue"),
                            meta: {
                                title: "新增医院",
                            },
                        },
                        {
                            path: ":id",
                            name: "HospitalsDetail",
                            component: () => import("@/views/hospitals/HospitalsDetail.vue"),
                            meta: {
                                title: "医院详情",
                            },
                        },
                        {
                            path: ":id/edit",
                            name: "HospitalsEdit",
                            component: () => import("@/views/hospitals/HospitalsEdit.vue"),
                            meta: {
                                title: "编辑医院",
                            },
                        },
                    ],
                },
                {
                    path: "logs",
                    name: "Logs",
                    component: () => import("@/views/Logs.vue"),
                    meta: {
                        requiresAuth: true,
                        title: "日志管理",
                        roles: ["admin"],
                    },
                },
                {
                    path: "users",
                    name: "Users",
                    component: () => import("@/views/Users.vue"),
                    meta: {
                        requiresAuth: true,
                        title: "用户管理",
                        roles: ["admin"],
                    },
                },
                {
                    path: "profile",
                    name: "Profile",
                    component: () => import("@/views/Profile.vue"),
                    meta: {
                        requiresAuth: true,
                        title: "个人资料",
                    },
                },
                {
                    path: "test-connection",
                    name: "TestConnection",
                    component: () => import("@/views/TestConnection.vue"),
                    meta: {
                        requiresAuth: true,
                        title: "连接测试",
                    },
                },
                {
                    path: "sync",
                    name: "Sync",
                    component: () => import("@/views/Sync.vue"),
                    meta: {
                        requiresAuth: true,
                        title: "同步管理",
                        roles: ["admin", "lab_user"],
                    },
                },
            ],
        },
        {
            path: "/:pathMatch(.*)*",
            name: "NotFound",
            component: () => import("@/views/NotFound.vue"),
            meta: {
                requiresAuth: false,
                title: "页面未找到",
            },
        },
    ],
});

// 路由守卫
router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore();

    // 设置页面标题
    if (to.meta.title) {
        document.title = `${to.meta.title} - 检验报告传输系统`;
    }

    // 检查是否需要认证
    if (to.meta.requiresAuth) {
        if (!authStore.isLoggedIn) {
            // 如果未登录，跳转到登录页
            next({
                name: "Login",
                query: { redirect: to.fullPath },
            });
            return;
        }

        // 检查角色权限
        if (to.meta.roles && authStore.user) {
            const hasPermission = (to.meta.roles as string[]).includes(authStore.user.role);
            if (!hasPermission) {
                // 没有权限，跳转到仪表板
                next({ name: "Dashboard" });
                return;
            }
        }
    } else {
        // 如果已登录，访问登录页时跳转到仪表板
        if (to.name === "Login" && authStore.isLoggedIn) {
            next({ name: "Dashboard" });
            return;
        }
    }

    next();
});

export default router;